import { useState, useEffect, useRef } from "react";
import { Printer, Download, CreditCard, CheckCircle2, AlertTriangle } from "lucide-react";
import { InvoicePaper } from "./BusinessViews.jsx";
import { remote } from "./remote.js";
import { intlMoney } from "./money.js";
import { pdfFile, safeFilename, downloadFile, printPdf } from "./deliver.js";

// What your client sees at  https://your-app/#/inv/<token>. No login, no access to anyone's other data:
// it only fetches this one invoice by its unguessable token. Opening it is what records "viewed".
export function PublicInvoice({ token, paidFlag }) {
  const [state, setState] = useState({ loading: true });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const asked = useRef(false);

  const load = (track) => remote.publicInvoice(token, track).then((d) => setState({ data: d })).catch((e) => setState({ error: e.message }));
  useEffect(() => { if (asked.current) return; asked.current = true; load(true); /* eslint-disable-next-line */ }, [token]);

  if (state.loading) return <div className="px-5 py-20 text-center text-ivory-dim text-sm">Loading invoice…</div>;
  if (state.error || !state.data) {
    return (<div className="px-5 py-20 text-center"><AlertTriangle size={22} className="mx-auto mb-3 text-brass" /><div className="text-sm font-semibold mb-1">This invoice can't be shown</div><div className="text-xs text-ivory-dim">{state.error || "The link may have been withdrawn."}</div></div>);
  }
  const { snapshot: s, currency, paid, payable } = state.data;
  const fmt = intlMoney(currency);
  const inv = { id: "public", number: s.invoice.number, issueDate: s.invoice.issueDate, dueDate: s.invoice.dueDate, notes: s.invoice.notes, billTo: s.invoice.billTo, taxRate: s.invoice.taxRate, status: paid ? "paid" : "sent", items: s.invoice.items.map((it, i) => ({ id: `i${i}`, ...it })) };
  const profile = { name: s.business.name || "Invoice", business: s.business, currency };
  const client = { name: s.client.name };

  async function pay() {
    setBusy(true); setMsg("");
    try { const { url } = await remote.payUrl(token); window.location.href = url; }
    catch (e) { setMsg(e.message); setBusy(false); }
  }
  const makeFile = async () => { const { invoicePdf } = await import("./pdfDocs.js"); return pdfFile(await invoicePdf(inv, client, profile, { branding: s.business.branding !== false }), safeFilename(`${inv.number}.pdf`)); };
  async function download() {
    try { downloadFile(await makeFile()); }
    catch (e) { setMsg(e.message); }
  }

  return (
    <div className="px-5 pt-8 pb-16">
      {paid ? (
        <div className="flex items-center gap-2 text-sm rounded-xl px-3.5 py-3 mb-4" style={{ background: "var(--brass-dim)" }}><CheckCircle2 size={16} style={{ color: "var(--sage)" }} /> This invoice has been paid. Thank you!</div>
      ) : paidFlag ? (
        <div className="text-sm rounded-xl px-3.5 py-3 mb-4" style={{ background: "var(--brass-dim)" }}>Thanks! We're confirming your payment, which usually takes a few seconds. <button onClick={() => load(false)} className="text-brass font-medium underline">Refresh</button></div>
      ) : null}

      <InvoicePaper inv={inv} client={client} profile={profile} fmt={fmt} />

      <div className="flex flex-col gap-2.5 mt-5">
        {payable && !paid && (
          <button onClick={pay} disabled={busy} className="w-full flex items-center justify-center gap-2 bg-gold-grad text-ink font-semibold rounded-full py-3 text-sm disabled:opacity-50"><CreditCard size={15} /> {busy ? "Opening secure checkout…" : "Pay by card"}</button>
        )}
        <div className="flex gap-2">
          <button onClick={download} className="flex-1 flex items-center justify-center gap-1.5 bg-card rounded-full py-2.5 text-sm font-medium"><Download size={14} /> Download PDF</button>
          <button onClick={() => printPdf(makeFile).catch((e) => setMsg(e.message))} className="flex-1 flex items-center justify-center gap-1.5 bg-card rounded-full py-2.5 text-sm font-medium"><Printer size={14} /> Print</button>
        </div>
        {msg && <div className="text-xs" style={{ color: "var(--rust)" }}>{msg}</div>}
        {payable && !paid && <div className="text-xs text-ivory-dim text-center">Card details are entered on Stripe's secure page and never touch this site.</div>}
        {s.business.branding !== false && <div className="text-xs text-ivory-dim text-center mt-2">Invoice sent with Balance &amp; Beyond</div>}
      </div>
    </div>
  );
}
